import { authClient } from './auth-client.js';

interface SessionQuery {
    data: unknown;
    isPending: boolean;
    isRefetching: boolean;
    refetch: () => Promise<void>;
}

interface SessionAtom {
    get: () => SessionQuery;
    listen: (listener: (state: SessionQuery) => void) => () => void;
}

const SETTLE_TIMEOUT_MS = 4_000;

/**
 * Better Auth keeps the last session read for about a second and only notifies
 * the client 10ms after sign-in. Navigating in that window lets the route guard
 * treat a signed-in user as logged out.
 */
function sessionAtom(): SessionAtom {
    return authClient.$store.atoms.session as SessionAtom;
}

function isSettled(state: SessionQuery): boolean {
    return !state.isPending && !state.isRefetching;
}

function waitUntilSettled(atom: SessionAtom): Promise<void> {
    if (isSettled(atom.get())) {
        return Promise.resolve();
    }
    return new Promise((resolve) => {
        let done = false;
        const finish = (): void => {
            if (done) {
                return;
            }
            done = true;
            window.clearTimeout(timeout);
            unsubscribe();
            resolve();
        };
        const unsubscribe = atom.listen(() => {
            if (isSettled(atom.get())) {
                finish();
            }
        });
        if (isSettled(atom.get())) {
            finish();
        }
        const timeout = window.setTimeout(finish, SETTLE_TIMEOUT_MS);
    });
}

/** Resolves true once the session atom holds a signed-in session. */
export async function waitForSignedInSession(): Promise<boolean> {
    const atom = sessionAtom();
    for (let attempt = 0; attempt < 2; attempt += 1) {
        await atom.get().refetch();
        await waitUntilSettled(atom);
        if (atom.get().data) {
            return true;
        }
    }
    return false;
}
